import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';

export const CTASection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useTranslation();

  return (
    <section ref={ref} className="relative overflow-hidden">
      {/* Dark Background Section */}
      <div className="section-dark py-20 lg:py-28">
        <div className="container mx-auto px-4 lg:px-8">
          {/* Main CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold mb-6">
              {t('cta.title')}
            </h2>
            <p className="text-accent-foreground/70 text-lg">
              {t('cta.subtitle')}
            </p>
          </motion.div>

          {/* Two CTA Cards */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* For Businesses */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-accent-foreground/5 border border-accent-foreground/10 rounded-2xl p-8 hover:bg-accent-foreground/10 transition-colors"
            >
              <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center mb-6">
                <Building2 size={24} className="text-primary" />
              </div>
              <h3 className="text-xl font-serif font-semibold mb-3">
                {t('cta.business.title')}
              </h3>
              <p className="text-accent-foreground/70 text-sm mb-6">
                {t('cta.business.description')}
              </p>
              <Button variant="gold" className="w-full" asChild>
                <Link to="/entreprises" className="gap-2">
                  {t('cta.business.button')}
                  <ArrowRight size={16} />
                </Link>
              </Button>
            </motion.div>

            {/* For Retirees */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-accent-foreground/5 border border-accent-foreground/10 rounded-2xl p-8 hover:bg-accent-foreground/10 transition-colors"
            >
              <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center mb-6">
                <User size={24} className="text-primary" />
              </div>
              <h3 className="text-xl font-serif font-semibold mb-3">
                {t('cta.retiree.title')}
              </h3>
              <p className="text-accent-foreground/70 text-sm mb-6">
                {t('cta.retiree.description')}
              </p>
              <Button 
                variant="outline" 
                className="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground" 
                asChild
              >
                <Link to="/retraites" className="gap-2">
                  {t('cta.retiree.button')}
                  <ArrowRight size={16} />
                </Link>
              </Button>
            </motion.div>
          </div>

          {/* Website URL */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-center mt-12"
          >
            <span className="text-accent-foreground/50 text-sm">
              www.sfretire.com
            </span>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
